import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  FileText, Plus, Pencil, Trash2, Copy, ChevronLeft, ChevronRight,
  TrendingUp, TrendingDown, DollarSign, ArrowUpDown, Save, Lock, Unlock, Loader2, RefreshCw
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const R = (v) => `R ${(v || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (v) => `${(v || 0).toFixed(1)}%`;

/**
 * Net Profit Target back-calculator.
 *
 * Inputs:
 *   revenue, cogs                    - current budgeted Revenue & COGS (R, ex-VAT)
 *   expensesTotal                    - sum of budgeted expense entries (informational
 *                                       categories excluded — see is_informational rule)
 *   target                           - desired net profit (string, R)
 *   onApplyRevenue(newRev, newCogs)  - parent setter; called when user clicks Apply
 *
 * Math:
 *   Let current GP% = (rev - cogs) / rev  (from the two inputs above)
 *   To hit target net profit:
 *     required_GP = target + expenses
 *     required_revenue = required_GP / GP%
 *     required_cogs    = required_revenue - required_GP  (preserves GP%)
 */
const NetProfitTargetCard = ({ revenue, cogs, expensesTotal, target, setTarget, onApplyRevenue }) => {
  const rev = parseFloat(revenue) || 0;
  const cgs = parseFloat(cogs) || 0;
  const tgt = parseFloat(target) || 0;
  const exp = expensesTotal || 0;

  const gpMargin = rev > 0 ? (rev - cgs) / rev : 0;            // e.g. 0.107
  const currentGP = rev - cgs;
  const currentNet = currentGP - exp;

  const canCalculate = rev > 0 && cgs >= 0 && gpMargin > 0 && tgt !== 0;
  const requiredGP = tgt + exp;
  const requiredRevenue = canCalculate ? requiredGP / gpMargin : 0;
  const requiredCogs = canCalculate ? Math.max(0, requiredRevenue - requiredGP) : 0;
  const revenueDelta = requiredRevenue - rev;

  return (
    <Card className="border-emerald-200 bg-emerald-50/40" data-testid="net-profit-target-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-semibold text-sm text-emerald-800 flex items-center gap-1.5">
              <TrendingUp className="h-4 w-4" /> Net Profit Target
            </h3>
            <p className="text-xs text-gray-600 mt-0.5">
              Type the net profit you want for the month — we'll work backwards from your
              current budgeted GP% and expenses to show the revenue you need.
            </p>
          </div>
          <div className="text-right text-xs text-gray-500 leading-tight">
            <div>GP%: <b className="text-gray-800">{(gpMargin * 100).toFixed(1)}%</b></div>
            <div>Expenses: <b className="text-gray-800">{R(exp)}</b></div>
            <div>Current net: <b className={currentNet >= 0 ? "text-emerald-700" : "text-red-600"}>{R(currentNet)}</b></div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-xs text-gray-500 uppercase font-medium">Target Net Profit (R)</label>
            <Input
              type="number" step="0.01"
              className="mt-1"
              placeholder="e.g. 20000"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              data-testid="net-profit-target-input"
            />
          </div>
          <div className="md:col-span-2 bg-white border border-emerald-200 rounded p-3 text-sm">
            {!canCalculate ? (
              <p className="text-gray-500 italic">
                Enter Budgeted Revenue, Cost of Sales and a Net Profit Target above.
                {rev > 0 && gpMargin <= 0 && " (Your COS ≥ Revenue — GP% must be positive.)"}
              </p>
            ) : (
              <div className="space-y-1" data-testid="net-profit-target-result">
                <div className="flex justify-between">
                  <span className="text-gray-600">Required Revenue:</span>
                  <span className="font-mono font-bold text-emerald-700">{R(requiredRevenue)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Required Cost of Sales (same GP%):</span>
                  <span className="font-mono text-gray-700">{R(requiredCogs)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Revenue change needed:</span>
                  <span className={`font-mono font-semibold ${revenueDelta > 0 ? "text-orange-600" : "text-emerald-600"}`}>
                    {revenueDelta > 0 ? "+" : ""}{R(revenueDelta)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {canCalculate && (
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={() => onApplyRevenue(requiredRevenue, requiredCogs)}
              data-testid="apply-target-btn"
            >
              Apply to Budgeted Revenue & COS
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function IncomeStatement() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [view, setView] = useState("single"); // single | multi
  const [report, setReport] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("report");

  // Category management
  const [catDialog, setCatDialog] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [catForm, setCatForm] = useState({ name: "", type: "variable" });

  // Budget/Expense entry
  const [entryMonth, setEntryMonth] = useState(now.getMonth() + 1);
  const [entryYear, setEntryYear] = useState(now.getFullYear());
  const [budgetEntries, setBudgetEntries] = useState({});
  const [expenseEntries, setExpenseEntries] = useState({});
  const [grvMonthTotal, setGrvMonthTotal] = useState(0);
  const [forecastDialog, setForecastDialog] = useState(false);
  const [forecastLookback, setForecastLookback] = useState(3);
  const [forecastGrowth, setForecastGrowth] = useState(5);
  const [forecastBusy, setForecastBusy] = useState(false);
  const [forecastPreview, setForecastPreview] = useState(null);
  const [revCogsBudget, setRevCogsBudget] = useState({ revenue: "", cogs: "" });
  const [netProfitTarget, setNetProfitTarget] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year, view };
      if (view === "single") params.month = month;
      const res = await axios.get(`${API}/income-statement/report`, { params });
      setReport(res.data);
      setCategories(res.data.categories || []);
    } catch (err) {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [year, month, view]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/income-statement/categories`);
      setCategories(res.data);
    } catch { /* handled in report */ }
  };

  const fetchEntryData = useCallback(async () => {
    try {
      const [expRes, budRes, grvRes] = await Promise.all([
        axios.get(`${API}/income-statement/expenses`, { params: { year: entryYear, month: entryMonth } }),
        axios.get(`${API}/income-statement/budgets`, { params: { year: entryYear, month: entryMonth } }),
        axios.get(`${API}/income-statement/grv-total`, { params: { year: entryYear, month: entryMonth } }),
      ]);
      const expMap = {};
      expRes.data.forEach(e => { expMap[e.category_id] = { amount: e.amount, notes: e.notes || "" }; });
      setExpenseEntries(expMap);

      const grvTotal = grvRes.data?.total || 0;
      setGrvMonthTotal(grvTotal);

      // Auto-prefill the Stock Purchases category from GRV total when no
      // value has been saved yet for the month
      if (grvTotal > 0) {
        const stockCat = (categories || []).find((c) =>
          (c.name || "").toLowerCase().includes("stock purchase")
        );
        if (stockCat && !expMap[stockCat.id]) {
          setExpenseEntries(prev => ({
            ...prev,
            [stockCat.id]: { amount: grvTotal, notes: "Auto from GRV" }
          }));
        }
      }

      const budMap = {};
      let revBud = "", cogsBud = "", targetBud = "";
      budRes.data.forEach(b => {
        if (b.category_id === "__revenue__") revBud = b.amount;
        else if (b.category_id === "__cogs__") cogsBud = b.amount;
        else if (b.category_id === "__net_profit_target__") targetBud = b.amount;
        else budMap[b.category_id] = b.amount;
      });
      setBudgetEntries(budMap);
      setRevCogsBudget({ revenue: revBud, cogs: cogsBud });
      setNetProfitTarget(targetBud === 0 ? "" : targetBud);
    } catch {
      toast.error("Failed to load entry data");
    }
  }, [entryYear, entryMonth, categories]);

  useEffect(() => {
    if (activeTab === "expenses" || activeTab === "budgets") {
      fetchEntryData();
    }
  }, [activeTab, fetchEntryData]);

  // ---- Category CRUD ----
  const handleSaveCat = async () => {
    try {
      if (editCat) {
        await saveWithRetry(() => axios.put(`${API}/income-statement/categories/${editCat.id}`, catForm));
        toast.success("Category updated");
      } else {
        await saveWithRetry(() => axios.post(`${API}/income-statement/categories`, catForm));
        toast.success("Category created");
      }
      setCatDialog(false);
      setEditCat(null);
      setCatForm({ name: "", type: "variable" });
      fetchCategories();
      fetchReport();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save category"));
    }
  };

  const handleDeleteCat = async (id, name) => {
    if (!window.confirm(`Delete category "${name}"? This is a soft delete.`)) return;
    try {
      await saveWithRetry(() => axios.delete(`${API}/income-statement/categories/${id}`));
      toast.success("Category deleted");
      fetchCategories();
      fetchReport();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to delete"));
    }
  };

  // ---- Save expenses ----
  const handleSaveExpenses = async () => {
    setSaving(true);
    try {
      const promises = categories.map(cat => {
        const entry = expenseEntries[cat.id];
        if (entry && (entry.amount || entry.amount === 0)) {
          return axios.post(`${API}/income-statement/expenses`, {
            category_id: cat.id, year: entryYear, month: entryMonth,
            amount: parseFloat(entry.amount) || 0, notes: entry.notes || "",
          });
        }
        return null;
      }).filter(Boolean);
      await Promise.all(promises);
      toast.success("Expenses saved");
      fetchReport();
    } catch (err) {
      toast.error("Failed to save expenses");
    } finally {
      setSaving(false);
    }
  };

  // ---- Save budgets ----
  const handleSaveBudgets = async () => {
    setSaving(true);
    try {
      const promises = categories.map(cat => {
        const amount = budgetEntries[cat.id];
        if (amount || amount === 0) {
          return axios.post(`${API}/income-statement/budgets`, {
            category_id: cat.id, year: entryYear, month: entryMonth,
            amount: parseFloat(amount) || 0,
          });
        }
        return null;
      }).filter(Boolean);

      // Also save revenue/cogs/target budgets
      if (revCogsBudget.revenue !== "" || revCogsBudget.cogs !== "" || netProfitTarget !== "") {
        promises.push(
          axios.post(`${API}/income-statement/budgets/revenue-cogs`, {
            year: entryYear, month: entryMonth,
            budget_revenue: revCogsBudget.revenue !== "" ? parseFloat(revCogsBudget.revenue) : null,
            budget_cogs: revCogsBudget.cogs !== "" ? parseFloat(revCogsBudget.cogs) : null,
            net_profit_target: netProfitTarget !== "" ? parseFloat(netProfitTarget) : null,
          })
        );
      }

      await Promise.all(promises);
      toast.success("Budgets saved");
      fetchReport();
    } catch (err) {
      toast.error("Failed to save budgets");
    } finally {
      setSaving(false);
    }
  };

  // ---- Copy previous month ----
  const handleCopyPrevBudgets = async () => {
    try {
      const res = await axios.post(`${API}/income-statement/budgets/copy-previous`, {
        year: entryYear, month: entryMonth,
      });
      toast.success(res.data.message);
      fetchEntryData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to copy");
    }
  };

  const handleApplyFixed = async () => {
    try {
      const res = await axios.post(`${API}/income-statement/expenses/apply-fixed`, {
        year: entryYear, month: entryMonth,
      });
      toast.success(res.data.message);
      fetchEntryData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply fixed");
    }
  };

  // ---- Forecast budget from prior months ----
  const handleRunForecast = async () => {
    setForecastBusy(true);
    setForecastPreview(null);
    try {
      const res = await axios.get(`${API}/income-statement/forecast`, {
        params: {
          year: entryYear,
          month: entryMonth,
          lookback_months: forecastLookback,
          growth_pct: forecastGrowth,
        },
      });
      setForecastPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to run forecast");
    } finally {
      setForecastBusy(false);
    }
  };

  const handleApplyForecast = () => {
    if (!forecastPreview) return;
    setRevCogsBudget({
      revenue: forecastPreview.forecast_revenue || 0,
      cogs: forecastPreview.forecast_cogs || 0,
    });
    toast.success("Forecast applied — review and click Save to confirm");
    setForecastDialog(false);
    setForecastPreview(null);
  };

  // ---- Period selector helpers ----
  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  // ---- Render ----
  const monthData = report?.months?.[String(month)] || null;

  return (
    <div className="space-y-4 pb-20" data-testid="income-statement-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="h-7 w-7 text-emerald-700" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="income-statement-title">
              Income Statement
            </h1>
            <p className="text-sm text-gray-500">Profit & Loss Report</p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="report" data-testid="tab-report">P&L Report</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Enter Expenses</TabsTrigger>
          <TabsTrigger value="budgets" data-testid="tab-budgets">Enter Budget</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
        </TabsList>

        {/* ==================== P&L REPORT TAB ==================== */}
        <TabsContent value="report" className="mt-4">
          {/* Period controls */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Select value={view} onValueChange={setView}>
              <SelectTrigger className="w-[160px]" data-testid="view-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Single Month</SelectItem>
                <SelectItem value="multi">Full Year</SelectItem>
              </SelectContent>
            </Select>

            {view === "single" && (
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" onClick={prevMonth} data-testid="prev-month-btn">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="font-semibold text-sm min-w-[100px] text-center">
                  {MONTHS[month - 1]} {year}
                </span>
                <Button variant="outline" size="icon" onClick={nextMonth} data-testid="next-month-btn">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            {view === "multi" && (
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" onClick={() => setYear(y => y - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="font-semibold text-sm min-w-[60px] text-center">{year}</span>
                <Button variant="outline" size="icon" onClick={() => setYear(y => y + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            {loading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          </div>

          {/* Single Month View */}
          {view === "single" && monthData && (
            <SingleMonthReport data={monthData} ytd={report.ytd} />
          )}

          {/* Multi Month View */}
          {view === "multi" && report && (
            <MultiMonthReport report={report} categories={categories} />
          )}
        </TabsContent>

        {/* ==================== EXPENSES TAB ==================== */}
        <TabsContent value="expenses" className="mt-4">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <MonthYearSelector
                month={entryMonth} year={entryYear}
                setMonth={setEntryMonth} setYear={setEntryYear}
              />
              <Button variant="outline" size="sm" onClick={handleApplyFixed} data-testid="apply-fixed-btn">
                <Lock className="h-3 w-3 mr-1" /> Apply Fixed Expenses
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-[50px]">#</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="w-[80px] text-center">Type</TableHead>
                      <TableHead className="w-[180px] text-right">Amount (R)</TableHead>
                      <TableHead className="w-[200px]">Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map((cat, i) => {
                      const isStockPurchases = (cat.name || "").toLowerCase().includes("stock purchase");
                      const currentVal = parseFloat(expenseEntries[cat.id]?.amount) || 0;
                      const inSync = isStockPurchases && grvMonthTotal > 0 && Math.abs(currentVal - grvMonthTotal) < 0.01;
                      return (
                      <TableRow key={cat.id}>
                        <TableCell className="text-gray-400 text-xs">{i + 1}</TableCell>
                        <TableCell className="font-medium text-sm">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span>{cat.name}</span>
                            {isStockPurchases && grvMonthTotal > 0 && (
                              <>
                                <Badge
                                  variant="outline"
                                  className="text-[9px] border-amber-400 text-amber-700"
                                  title="Stock purchases reflect inventory bought, not sold. Already counted via Cost of Sales."
                                >
                                  Informational — not deducted
                                </Badge>
                                <Badge
                                  variant={inSync ? "default" : "outline"}
                                  className={`text-[10px] ${inSync ? "bg-green-600 hover:bg-green-600" : "border-gray-400 text-gray-600"}`}
                                  data-testid="grv-link-badge"
                                >
                                  {inSync ? "Synced with GRV" : "Manual"}
                                </Badge>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-6 text-[11px] px-2"
                                  onClick={() =>
                                    setExpenseEntries(prev => ({
                                      ...prev,
                                      [cat.id]: { ...prev[cat.id], amount: grvMonthTotal, notes: prev[cat.id]?.notes || "Auto from GRV" }
                                    }))
                                  }
                                  data-testid="sync-grv-btn"
                                  title={`Pull R${grvMonthTotal.toLocaleString()} from GRV total`}
                                >
                                  <RefreshCw className="h-3 w-3 mr-1" />
                                  Sync R{grvMonthTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={cat.type === "fixed" ? "default" : "secondary"} className="text-[10px]">
                            {cat.type === "fixed" ? <Lock className="h-2.5 w-2.5 mr-0.5" /> : <Unlock className="h-2.5 w-2.5 mr-0.5" />}
                            {cat.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number" step="0.01" min="0"
                            className="text-right h-8 text-sm"
                            placeholder="0.00"
                            value={expenseEntries[cat.id]?.amount ?? ""}
                            onChange={(e) => setExpenseEntries(prev => ({
                              ...prev,
                              [cat.id]: { ...prev[cat.id], amount: e.target.value }
                            }))}
                            data-testid={`expense-amount-${cat.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            className="h-8 text-sm"
                            placeholder="Notes..."
                            value={expenseEntries[cat.id]?.notes ?? ""}
                            onChange={(e) => setExpenseEntries(prev => ({
                              ...prev,
                              [cat.id]: { ...prev[cat.id], notes: e.target.value }
                            }))}
                          />
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={handleSaveExpenses} disabled={saving} data-testid="save-expenses-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Expenses for {MONTHS[entryMonth - 1]} {entryYear}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* ==================== BUDGETS TAB ==================== */}
        <TabsContent value="budgets" className="mt-4">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <MonthYearSelector
                month={entryMonth} year={entryYear}
                setMonth={setEntryMonth} setYear={setEntryYear}
              />
              <Button variant="outline" size="sm" onClick={handleCopyPrevBudgets} data-testid="copy-prev-btn">
                <Copy className="h-3 w-3 mr-1" /> Copy from Previous Month
              </Button>
              <Button variant="outline" size="sm" onClick={() => setForecastDialog(true)} data-testid="forecast-btn"
                className="border-violet-300 text-violet-700 hover:bg-violet-50">
                <TrendingUp className="h-3 w-3 mr-1" /> Forecast from Previous Months
              </Button>
            </div>

            {/* Revenue & COGS budget */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <h3 className="font-semibold text-sm text-gray-700">Revenue & Cost of Sales Budget</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 uppercase font-medium">Budgeted Revenue (R)</label>
                    <Input
                      type="number" step="0.01" min="0"
                      className="mt-1"
                      placeholder="e.g. 500000"
                      value={revCogsBudget.revenue}
                      onChange={(e) => setRevCogsBudget(prev => ({ ...prev, revenue: e.target.value }))}
                      data-testid="budget-revenue-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase font-medium">Budgeted Cost of Sales (R)</label>
                    <Input
                      type="number" step="0.01" min="0"
                      className="mt-1"
                      placeholder="e.g. 400000"
                      value={revCogsBudget.cogs}
                      onChange={(e) => setRevCogsBudget(prev => ({ ...prev, cogs: e.target.value }))}
                      data-testid="budget-cogs-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Net Profit Target — back-calculator */}
            <NetProfitTargetCard
              revenue={revCogsBudget.revenue}
              cogs={revCogsBudget.cogs}
              expensesTotal={(() => {
                return (categories || []).reduce((sum, c) => {
                  const isInformational = (c.name || "").toLowerCase().includes("stock purchase");
                  if (isInformational) return sum;
                  const v = parseFloat(budgetEntries[c.id]);
                  return sum + (isNaN(v) ? 0 : v);
                }, 0);
              })()}
              target={netProfitTarget}
              setTarget={setNetProfitTarget}
              onApplyRevenue={(newRevenue, newCogs) => {
                setRevCogsBudget({
                  revenue: newRevenue.toFixed(2),
                  cogs: newCogs.toFixed(2),
                });
              }}
            />

            {/* Expense budgets */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-[50px]">#</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="w-[80px] text-center">Type</TableHead>
                      <TableHead className="w-[180px] text-right">Budget Amount (R)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map((cat, i) => (
                      <TableRow key={cat.id}>
                        <TableCell className="text-gray-400 text-xs">{i + 1}</TableCell>
                        <TableCell className="font-medium text-sm">{cat.name}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant={cat.type === "fixed" ? "default" : "secondary"} className="text-[10px]">
                            {cat.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number" step="0.01" min="0"
                            className="text-right h-8 text-sm"
                            placeholder="0.00"
                            value={budgetEntries[cat.id] ?? ""}
                            onChange={(e) => setBudgetEntries(prev => ({
                              ...prev, [cat.id]: e.target.value,
                            }))}
                            data-testid={`budget-amount-${cat.id}`}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={handleSaveBudgets} disabled={saving} data-testid="save-budgets-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Budgets for {MONTHS[entryMonth - 1]} {entryYear}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* ==================== CATEGORIES TAB ==================== */}
        <TabsContent value="categories" className="mt-4">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-gray-700">Expense Categories</h3>
              <Button size="sm" onClick={() => { setEditCat(null); setCatForm({ name: "", type: "variable" }); setCatDialog(true); }} data-testid="add-category-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Category
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-[50px]">#</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="w-[100px] text-center">Type</TableHead>
                      <TableHead className="w-[100px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map((cat, i) => (
                      <TableRow key={cat.id}>
                        <TableCell className="text-gray-400 text-xs">{i + 1}</TableCell>
                        <TableCell className="font-medium">{cat.name}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant={cat.type === "fixed" ? "default" : "secondary"}>
                            {cat.type === "fixed" ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                            {cat.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7"
                              onClick={() => { setEditCat(cat); setCatForm({ name: cat.name, type: cat.type }); setCatDialog(true); }}
                              data-testid={`edit-cat-${cat.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteCat(cat.id, cat.name)}
                              data-testid={`delete-cat-${cat.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Category Dialog */}
      <Dialog open={catDialog} onOpenChange={setCatDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editCat ? "Edit Category" : "Add Category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-500">Name</label>
              <Input
                value={catForm.name}
                onChange={(e) => setCatForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Category name"
                data-testid="cat-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Type</label>
              <Select value={catForm.type} onValueChange={(v) => setCatForm(prev => ({ ...prev, type: v }))}>
                <SelectTrigger data-testid="cat-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed (same every month)</SelectItem>
                  <SelectItem value="variable">Variable (changes monthly)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveCat} disabled={!catForm.name.trim()} data-testid="save-cat-btn">
              {editCat ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forecast Budget Dialog */}
      <Dialog open={forecastDialog} onOpenChange={(o) => { setForecastDialog(o); if (!o) setForecastPreview(null); }}>
        <DialogContent className="max-w-md" data-testid="forecast-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-violet-600" />
              Forecast Budget for {MONTHS[entryMonth - 1]} {entryYear}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-xs text-gray-600">
              Averages your actual revenue & cost of sales over the past months,
              then inflates by the growth rate to suggest a budget for this month.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-500">Lookback (months)</label>
                <Select value={String(forecastLookback)} onValueChange={(v) => setForecastLookback(parseInt(v))}>
                  <SelectTrigger data-testid="forecast-lookback-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">Last 3 months</SelectItem>
                    <SelectItem value="6">Last 6 months</SelectItem>
                    <SelectItem value="12">Last 12 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Growth (%)</label>
                <Input
                  type="number"
                  step="0.1"
                  value={forecastGrowth}
                  onChange={(e) => setForecastGrowth(parseFloat(e.target.value) || 0)}
                  data-testid="forecast-growth-input"
                />
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handleRunForecast}
              disabled={forecastBusy}
              className="w-full"
              data-testid="run-forecast-btn"
            >
              {forecastBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Calculate Forecast
            </Button>

            {forecastPreview && (
              <div className="bg-violet-50 border border-violet-200 rounded p-3 space-y-2 text-sm">
                <div className="text-xs text-gray-600">
                  Based on: {forecastPreview.months_used?.length ? forecastPreview.months_used.join(", ") : "No data"}
                  {" · "}+{forecastPreview.growth_pct}% growth
                </div>
                {forecastPreview.message ? (
                  <div className="text-amber-700">{forecastPreview.message}</div>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Average Revenue:</span>
                      <span>R {forecastPreview.avg_revenue?.toLocaleString("en-ZA", { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Average COGS:</span>
                      <span>R {forecastPreview.avg_cogs?.toLocaleString("en-ZA", { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="border-t border-violet-200 pt-2 mt-2 space-y-1">
                      <div className="flex justify-between font-semibold text-violet-800">
                        <span>Forecast Revenue:</span>
                        <span>R {forecastPreview.forecast_revenue?.toLocaleString("en-ZA", { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between font-semibold text-violet-800">
                        <span>Forecast COGS:</span>
                        <span>R {forecastPreview.forecast_cogs?.toLocaleString("en-ZA", { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForecastDialog(false)}>Cancel</Button>
            <Button
              onClick={handleApplyForecast}
              disabled={!forecastPreview || forecastPreview.message}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="apply-forecast-btn"
            >
              Apply to Budget
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ==================== Sub-components ====================

function MonthYearSelector({ month, year, setMonth, setYear }) {
  return (
    <div className="flex items-center gap-2">
      <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
        <SelectTrigger className="w-[120px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MONTHS.map((m, i) => (
            <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
        <SelectTrigger className="w-[100px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {[2024, 2025, 2026, 2027].map(y => (
            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}


function SingleMonthReport({ data, ytd }) {
  const gp = data.gross_profit;
  const np = data.net_profit;

  // Split expenses by type
  const fixedExpenses = data.expenses.filter(e => e.category_type === "fixed");
  const variableExpenses = data.expenses.filter(e => e.category_type === "variable");

  return (
    <div className="space-y-4" data-testid="single-month-report">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Revenue" value={data.revenue} budget={data.budget_revenue} icon={DollarSign} color="blue" subtitle="excl. VAT" />
        <SummaryCard label="Cost of Sales" value={data.cogs} budget={data.budget_cogs} icon={ArrowUpDown} color="orange" subtitle="excl. VAT" />
        <SummaryCard label="Gross Profit" value={gp} pct={data.gross_profit_pct} icon={TrendingUp} color="emerald" />
        <SummaryCard label="Net Profit" value={np} pct={data.net_profit_pct} icon={np >= 0 ? TrendingUp : TrendingDown} color={np >= 0 ? "emerald" : "red"} />
      </div>

      {/* Detailed P&L Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-800 text-white">
                <TableHead className="text-white font-bold">Description</TableHead>
                <TableHead className="text-white font-bold text-right">Actual</TableHead>
                <TableHead className="text-white font-bold text-right">Budget</TableHead>
                <TableHead className="text-white font-bold text-right">Variance</TableHead>
                <TableHead className="text-white font-bold text-right">YTD</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Revenue */}
              <TableRow className="bg-blue-50 font-bold">
                <TableCell>SALES REVENUE</TableCell>
                <TableCell className="text-right">{R(data.revenue)}</TableCell>
                <TableCell className="text-right text-gray-500">{R(data.budget_revenue)}</TableCell>
                <TableCell className={`text-right ${data.revenue - data.budget_revenue >= 0 ? "text-green-700" : "text-red-600"}`}>
                  {R(data.revenue - data.budget_revenue)}
                </TableCell>
                <TableCell className="text-right">{R(ytd?.revenue)}</TableCell>
              </TableRow>

              {/* COGS */}
              <TableRow className="bg-orange-50 font-bold">
                <TableCell>COST OF SALES</TableCell>
                <TableCell className="text-right">{R(data.cogs)}</TableCell>
                <TableCell className="text-right text-gray-500">{R(data.budget_cogs)}</TableCell>
                <TableCell className={`text-right ${data.budget_cogs - data.cogs >= 0 ? "text-green-700" : "text-red-600"}`}>
                  {R(data.budget_cogs - data.cogs)}
                </TableCell>
                <TableCell className="text-right">{R(ytd?.cogs)}</TableCell>
              </TableRow>

              {/* Gross Profit */}
              <TableRow className="bg-emerald-100 font-bold border-t-2 border-emerald-300">
                <TableCell>GROSS PROFIT ({pct(data.gross_profit_pct)})</TableCell>
                <TableCell className="text-right">{R(gp)}</TableCell>
                <TableCell className="text-right text-gray-500">{R(data.budget_gross)}</TableCell>
                <TableCell className={`text-right ${gp - data.budget_gross >= 0 ? "text-green-700" : "text-red-600"}`}>
                  {R(gp - data.budget_gross)}
                </TableCell>
                <TableCell className="text-right">{R(ytd?.gross_profit)}</TableCell>
              </TableRow>

              {/* Spacer */}
              <TableRow><TableCell colSpan={5} className="h-2 p-0 bg-gray-50" /></TableRow>

              {/* Fixed Expenses Header */}
              {fixedExpenses.length > 0 && (
                <>
                  <TableRow className="bg-gray-100">
                    <TableCell colSpan={5} className="font-bold text-gray-700 text-xs uppercase tracking-wider py-1.5">
                      <Lock className="h-3 w-3 inline mr-1" /> Fixed Expenses
                    </TableCell>
                  </TableRow>
                  {fixedExpenses.map(exp => (
                    <ExpenseRow key={exp.category_id} exp={exp} ytdCats={ytd?.expenses_by_category} />
                  ))}
                </>
              )}

              {/* Variable Expenses Header */}
              {variableExpenses.length > 0 && (
                <>
                  <TableRow className="bg-gray-100">
                    <TableCell colSpan={5} className="font-bold text-gray-700 text-xs uppercase tracking-wider py-1.5">
                      <Unlock className="h-3 w-3 inline mr-1" /> Variable Expenses
                    </TableCell>
                  </TableRow>
                  {variableExpenses.map(exp => (
                    <ExpenseRow key={exp.category_id} exp={exp} ytdCats={ytd?.expenses_by_category} />
                  ))}
                </>
              )}

              {/* Total Expenses */}
              <TableRow className="bg-red-50 font-bold border-t-2 border-red-300">
                <TableCell>TOTAL EXPENSES</TableCell>
                <TableCell className="text-right">{R(data.total_expenses)}</TableCell>
                <TableCell className="text-right text-gray-500">{R(data.budget_total_expenses)}</TableCell>
                <TableCell className={`text-right ${data.budget_total_expenses - data.total_expenses >= 0 ? "text-green-700" : "text-red-600"}`}>
                  {R(data.budget_total_expenses - data.total_expenses)}
                </TableCell>
                <TableCell className="text-right">{R(ytd?.total_expenses)}</TableCell>
              </TableRow>

              {/* Spacer */}
              <TableRow><TableCell colSpan={5} className="h-2 p-0 bg-gray-50" /></TableRow>

              {/* Net Profit */}
              <TableRow className={`font-bold text-lg border-t-4 ${np >= 0 ? "bg-emerald-100 border-emerald-400" : "bg-red-100 border-red-400"}`}>
                <TableCell>NET PROFIT / (LOSS) ({pct(data.net_profit_pct)})</TableCell>
                <TableCell className="text-right">{R(np)}</TableCell>
                <TableCell className="text-right text-gray-500">{R(data.budget_net_profit)}</TableCell>
                <TableCell className={`text-right ${np - data.budget_net_profit >= 0 ? "text-green-700" : "text-red-600"}`}>
                  {R(np - data.budget_net_profit)}
                </TableCell>
                <TableCell className="text-right">{R(ytd?.net_profit)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}


function ExpenseRow({ exp, ytdCats }) {
  const ytdVal = (ytdCats || []).find(c => c.category_id === exp.category_id)?.actual || 0;
  const hasValue = exp.actual > 0 || exp.budget > 0;
  if (!hasValue && ytdVal === 0) return null; // Hide empty rows

  return (
    <TableRow className={`hover:bg-gray-50 ${exp.is_informational ? "bg-amber-50/40 italic" : ""}`}>
      <TableCell className="pl-6 text-sm">
        <div className="flex items-center gap-2 flex-wrap">
          <span>{exp.category_name}</span>
          {exp.is_informational && (
            <Badge variant="outline" className="text-[9px] border-amber-400 text-amber-700">
              Informational only — not deducted
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right text-sm">{exp.actual > 0 ? R(exp.actual) : "-"}</TableCell>
      <TableCell className="text-right text-sm text-gray-500">{exp.budget > 0 ? R(exp.budget) : "-"}</TableCell>
      <TableCell className={`text-right text-sm ${exp.variance >= 0 ? "text-green-700" : "text-red-600"}`}>
        {(exp.actual > 0 || exp.budget > 0) ? R(exp.variance) : "-"}
      </TableCell>
      <TableCell className="text-right text-sm">{ytdVal > 0 ? R(ytdVal) : "-"}</TableCell>
    </TableRow>
  );
}


function SummaryCard({ label, value, budget, pct: pctVal, icon: Icon, color, subtitle }) {
  const colorMap = {
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    orange: "bg-orange-50 border-orange-200 text-orange-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    red: "bg-red-50 border-red-200 text-red-700",
  };

  return (
    <Card className={`border ${colorMap[color]}`}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium uppercase opacity-70">{label}</span>
          <Icon className="h-4 w-4 opacity-50" />
        </div>
        <div className="text-lg font-bold">{R(value)}</div>
        {subtitle && <div className="text-[10px] opacity-60">{subtitle}</div>}
        {pctVal !== undefined && <div className="text-xs opacity-70">{pct(pctVal)}</div>}
        {budget !== undefined && budget > 0 && (
          <div className="text-[10px] opacity-60 mt-0.5">Budget: {R(budget)}</div>
        )}
      </CardContent>
    </Card>
  );
}


function MultiMonthReport({ report, categories }) {
  const months = report.months;
  const ytd = report.ytd;

  return (
    <div className="overflow-x-auto" data-testid="multi-month-report">
      <Table className="min-w-[1200px]">
        <TableHeader>
          <TableRow className="bg-gray-800">
            <TableHead className="text-white font-bold sticky left-0 bg-gray-800 z-10 min-w-[180px]">Description</TableHead>
            {MONTHS.map((m, i) => (
              <TableHead key={i} className="text-white font-bold text-right text-xs min-w-[90px]">{m}</TableHead>
            ))}
            <TableHead className="text-white font-bold text-right min-w-[100px]">YTD</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* Revenue */}
          <MultiRow label="SALES REVENUE" bold bg="bg-blue-50"
            values={MONTHS.map((_, i) => months[String(i + 1)]?.revenue || 0)}
            ytd={ytd.revenue}
          />
          {/* COGS */}
          <MultiRow label="COST OF SALES" bold bg="bg-orange-50"
            values={MONTHS.map((_, i) => months[String(i + 1)]?.cogs || 0)}
            ytd={ytd.cogs}
          />
          {/* Gross Profit */}
          <MultiRow label="GROSS PROFIT" bold bg="bg-emerald-100"
            values={MONTHS.map((_, i) => months[String(i + 1)]?.gross_profit || 0)}
            ytd={ytd.gross_profit}
          />
          {/* Spacer */}
          <TableRow><TableCell colSpan={14} className="h-1 p-0 bg-gray-100" /></TableRow>

          {/* Expense categories */}
          {categories.map(cat => {
            const vals = MONTHS.map((_, i) => {
              const md = months[String(i + 1)];
              const exp = (md?.expenses || []).find(e => e.category_id === cat.id);
              return exp?.actual || 0;
            });
            const ytdVal = (ytd.expenses_by_category || []).find(c => c.category_id === cat.id)?.actual || 0;
            const hasAny = vals.some(v => v > 0) || ytdVal > 0;
            if (!hasAny) return null;
            return <MultiRow key={cat.id} label={cat.name} indent values={vals} ytd={ytdVal} />;
          })}

          {/* Total Expenses */}
          <MultiRow label="TOTAL EXPENSES" bold bg="bg-red-50"
            values={MONTHS.map((_, i) => months[String(i + 1)]?.total_expenses || 0)}
            ytd={ytd.total_expenses}
          />
          {/* Spacer */}
          <TableRow><TableCell colSpan={14} className="h-1 p-0 bg-gray-100" /></TableRow>

          {/* Net Profit */}
          <MultiRow label="NET PROFIT / (LOSS)" bold bg="bg-emerald-100"
            values={MONTHS.map((_, i) => months[String(i + 1)]?.net_profit || 0)}
            ytd={ytd.net_profit}
          />
        </TableBody>
      </Table>
    </div>
  );
}


function MultiRow({ label, values, ytd, bold, bg, indent }) {
  return (
    <TableRow className={`${bg || ""} ${bold ? "font-bold" : ""}`}>
      <TableCell className={`sticky left-0 ${bg || "bg-white"} z-10 text-sm ${indent ? "pl-6" : ""}`}>
        {label}
      </TableCell>
      {values.map((v, i) => (
        <TableCell key={i} className={`text-right text-xs ${v < 0 ? "text-red-600" : ""}`}>
          {v !== 0 ? R(v) : "-"}
        </TableCell>
      ))}
      <TableCell className={`text-right text-xs font-bold ${ytd < 0 ? "text-red-600" : ""}`}>
        {R(ytd)}
      </TableCell>
    </TableRow>
  );
}
